import React, { type FC, memo } from "react";
import { shallowEqual } from "react-redux";
import clsx from "clsx";
import Text from "../../../../../shared-components/css-modules/lexus/atoms/Text";
import SummaryPanel from "./SummaryPanel";
import styles from "./overview/Overview.module.scss";
import { useCarConfigSelector } from "../../../../redux/store";
import baseSectionStyles from "../section-wrapper/SectionWrapper.module.scss";
import ExteriorSlider from "../spin/exterior-slider/ExteriorSlider";
import ConfigCtas from "../cta/ConfigCtas";
import { useCarConfigCCISNormalization, useCarIds } from "../../../../hooks/useAphCCISNormalization";
import {
    getChargingVisual,
    getColorVisual,
    getColorBackgroundVisual,
    getEngineVisual,
    getPacksVisual,
    getUpholsteryVisual,
    getWheelsVisual,
} from "../../../../utils/visualUtils";
import { getCarImage } from "../../../../../shared-logic/utils/imageUtils";
import { CarConfigLabel, getCarConfigLabel } from "../../../../utils/labelUtils";

const Summary: FC = () => {
    const car = useCarConfigSelector((state) => state.car);
    const carConfigMotorization = useCarConfigSelector((state) => state.carConfigMotorization);
    const wheel = useCarConfigSelector((state) => state.basket.wheel);
    const upholstery = useCarConfigSelector((state) => state.basket.upholstery);
    const homeChargers = useCarConfigSelector(
        (state) => state.basket.extras.filter((extra) => extra.isCharger),
        shallowEqual,
    );
    const packs = useCarConfigSelector((state) => state.basket.packs);
    const standardPacks = useCarConfigSelector(
        (state) => state.items.packs.filter((pack) => pack.standard),
        shallowEqual,
    );
    const extras = useCarConfigSelector(
        (state) => state.basket.extras.filter((extra) => !extra.isCharger),
        shallowEqual,
    );
    const colour = useCarConfigSelector((state) => state.basket.colour);
    const carIds = useCarIds();
    const carImageInput = useCarConfigCCISNormalization();
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const carConfigSettings = useCarConfigSelector((state) => state.carConfigSettings);

    const hasPacksOrExtras = packs.length !== 0 || extras.length !== 0 || standardPacks.length !== 0;
    const hasHomeChargers = homeChargers.length !== 0;
    const sliderIsInitialized = useCarConfigSelector((state) => state.car.initialized);

    const primaryVisual = getCarImage(
        commonSettings,
        12,
        carIds,
        {
            type: "webp",
            width: 1000,
            height: 700,
        },
        carImageInput,
    );

    const packVisual = getPacksVisual([...packs, ...standardPacks], extras, commonSettings);

    return (
        <>
            <Text tag="h2" variant="apheleia-h3" spacing="spacing-5">
                <CarConfigLabel label="carConfigOverviewTitle" />
            </Text>

            <figure className={baseSectionStyles["exterior-slider"]}>
                {sliderIsInitialized && (
                    <ExteriorSlider
                        frames={[13, 3, 0, 18]}
                        imageOptions={{ bgColour: "FFFFFF", width: 1600, height: 900, padding: 12 }}
                    />
                )}
            </figure>
            <div className={styles.mobilecta}>
                <ConfigCtas />
            </div>
            <div className={styles.overview}>
                <div>
                    <SummaryPanel />
                </div>
                <div>
                    <div className={styles.imagegrid}>
                        {sliderIsInitialized && (
                            <div>
                                <img
                                    src={primaryVisual.src}
                                    srcSet={primaryVisual.srcSet}
                                    alt=""
                                    className={styles["imagegrid-visual-main"]}
                                />
                            </div>
                        )}
                        <div
                            style={{
                                "--or-animation-delay": 0.25,
                            }}
                            className={styles["imagegrid-visual"]}
                            aria-describedby="visual-description-engine"
                        >
                            <img
                                src={getEngineVisual(car, carConfigMotorization, commonSettings)}
                                alt={getCarConfigLabel({ commonSettings, carConfigSettings }, "carConfigEngine")}
                            />
                            <div id="visual-description-engine" className={styles["imagegrid-visual-overlay"]}>
                                <CarConfigLabel label="carConfigEngine" />
                            </div>
                        </div>
                        <div
                            style={{
                                background: getColorBackgroundVisual(colour, commonSettings, carIds),
                                "--or-animation-delay": 0.35,
                            }}
                            className={styles["imagegrid-visual"]}
                            aria-describedby="visual-description-colour"
                        >
                            <img
                                src={getColorVisual(colour, commonSettings, carIds, carImageInput)}
                                alt={getCarConfigLabel({ commonSettings, carConfigSettings }, "carConfigColour")}
                            />
                            <div id="visual-description-colour" className={styles["imagegrid-visual-overlay"]}>
                                <CarConfigLabel label="carConfigColour" />
                            </div>
                        </div>
                        <div
                            style={{
                                "--or-animation-delay": 0.45,
                            }}
                            className={clsx(styles["imagegrid-visual"], styles["imagegrid-wheel"])}
                            aria-describedby="visual-description-wheels"
                        >
                            <img
                                src={getWheelsVisual(wheel, commonSettings)}
                                alt={getCarConfigLabel({ commonSettings, carConfigSettings }, "carConfigWheels")}
                            />
                            <div id="visual-description-wheels" className={styles["imagegrid-visual-overlay"]}>
                                <CarConfigLabel label="carConfigWheels" />
                            </div>
                        </div>
                        <div
                            style={{
                                "--or-animation-delay": 0.55,
                            }}
                            className={styles["imagegrid-visual"]}
                            aria-describedby="visual-description-upholstery"
                        >
                            <img
                                src={getUpholsteryVisual(upholstery, commonSettings)}
                                alt={getCarConfigLabel({ commonSettings, carConfigSettings }, "carConfigUpholstery")}
                            />
                            <div id="visual-description-upholstery" className={styles["imagegrid-visual-overlay"]}>
                                <CarConfigLabel label="carConfigUpholstery" />
                            </div>
                        </div>

                        {packVisual && (
                            <div
                                style={{
                                    "--or-animation-delay": 0.65,
                                }}
                                className={clsx(hasPacksOrExtras ? "" : styles.hide, styles["imagegrid-visual"])}
                                aria-describedby="visual-description-packsorextras"
                            >
                                <img
                                    src={packVisual}
                                    alt={getCarConfigLabel(
                                        { commonSettings, carConfigSettings },
                                        "carConfigPacksAndExtras",
                                    )}
                                />
                                <div
                                    id="visual-description-packsorextras"
                                    className={styles["imagegrid-visual-overlay"]}
                                >
                                    <CarConfigLabel label="carConfigPacksAndExtras" />
                                </div>
                            </div>
                        )}

                        <div
                            style={{
                                "--or-animation-delay": 0.75,
                            }}
                            className={clsx(hasHomeChargers ? "" : styles.hide, styles["imagegrid-visual"])}
                            aria-describedby="visual-description-homeChargers"
                        >
                            <img
                                src={getChargingVisual(homeChargers, commonSettings)}
                                alt={getCarConfigLabel(
                                    { commonSettings, carConfigSettings },
                                    "carConfigChargingAtHome",
                                )}
                            />
                            <div id="visual-description-homeChargers" className={styles["imagegrid-visual-overlay"]}>
                                <CarConfigLabel label="carConfigChargingAtHome" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default memo(Summary);
